import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, courseImageUrl, type Course, type CourseEdition, type Diplomado, type Enrollment } from "@/lib/api";
import NuevoDiplomadoModal from "@/components/feature/NuevoDiplomadoModal";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

export default function DiplomadosPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [diplomados, setDiplomados] = useState<Diplomado[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [diplomadosData, enrollmentsData] = await Promise.all([
        api.getDiplomados({ includeDeleted: showDeleted }),
        api.getEnrollments(),
      ]);
      setDiplomados(diplomadosData);
      setEnrollments(enrollmentsData);
    } catch {
      setDiplomados([]);
      setEnrollments([]);
    }
    setLoading(false);
  }, [showDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filterCourses = useMemo(
    () => diplomados.map((d) => ({ ...d, editions: undefined }) as unknown as Course),
    [diplomados]
  );

  const filterEditions = useMemo(
    () => diplomados.flatMap((d) => (d.editions || []).map((ed) => ({
      id: ed.id,
      course_id: d.id,
      edition_label: ed.edition_label,
      start_date: ed.start_date,
      end_date: ed.end_date,
      location: ed.location,
      instructor_id: ed.instructor_id,
      evaluator_id: ed.evaluator_id,
      capacity: ed.capacity,
      price: ed.price,
      status: ed.status,
      course: d as unknown as Course,
      instructor: ed.instructor,
      evaluator: ed.evaluator,
      service_type: "diplomado" as const,
    } satisfies CourseEdition))),
    [diplomados]
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return diplomados.filter((d) => {
      if (filters.category && d.category !== filters.category) return false;
      if (filters.courseId && d.id !== filters.courseId) return false;
      if (filters.editionId) {
        const hasEdition = d.editions?.some((ed) => ed.id === filters.editionId);
        if (!hasEdition) return false;
      }
      if (filters.month) {
        const hasMonth = d.editions?.some((ed) => matchesMonth(ed.start_date, filters.month));
        if (!hasMonth) return false;
      }
      if (activeFilter === "activo" && !d.active) return false;
      if (activeFilter === "inactivo" && d.active) return false;
      if (q) {
        const haystack = [d.name, d.description, d.category].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [diplomados, filters, activeFilter]);

  const getEnrollmentStats = (diplomadoId: string) => {
    const diplomadoEnrollments = enrollments.filter((e) => e.diplomado_id === diplomadoId);
    const total = diplomadoEnrollments.length;
    if (total === 0) return { enrolled: 0, attRate: 0, compRate: 0 };
    const present = diplomadoEnrollments.filter((e) => e.attendance_status === "presente").length;
    const completed = diplomadoEnrollments.filter((e) => e.certificate_issued).length;
    return {
      enrolled: total,
      attRate: Math.round((present / total) * 100),
      compRate: Math.round((completed / total) * 100),
    };
  };

  const handleToggleLanding = async (diplomado: Diplomado) => {
    setTogglingId(diplomado.id);
    try {
      const updated = await api.updateDiplomado(diplomado.id, {
        show_on_landing: !diplomado.show_on_landing,
      });
      setDiplomados((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    } catch {
      /* ignore */
    }
    setTogglingId(null);
  };

  const handleRestore = async (id: string) => {
    if (!confirm("¿Seguro que deseas recuperar este diplomado?")) return;
    await api.restoreDiplomado(id);
    loadData();
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Diplomados</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Catálogo de diplomados y programas extendidos</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn-primary flex items-center gap-2 cursor-pointer">
          <i className="ri-add-line"></i>
          Nuevo Diplomado
        </button>
      </div>

      {!loading && (
        <ListingFilters
          title="Filtros de diplomados"
          filters={filters}
          onChange={setFilters}
          courses={filterCourses}
          editions={filterEditions}
          showCourse={true}
          searchPlaceholder="Buscar diplomados..."
          resultCount={filtered.length}
          totalCount={diplomados.length}
          hint="Mes filtra por fecha de inicio de alguna edición del diplomado."
          categoryLabel="Tipo de diplomado"
          categoryAllLabel="Todos los tipos"
          courseLabel="Diplomado"
          courseAllLabel="Todos los diplomados"
          extraActive={Boolean(activeFilter || showDeleted)}
          onClearExtras={() => {
            setActiveFilter("");
            setShowDeleted(false);
          }}
          extras={
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">Estado</label>
                <select className="input-field" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="activo">Activos</option>
                  <option value="inactivo">Inactivos</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-text-primary)] transition-colors pb-2.5">
                  <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
                  Ver eliminados
                </label>
              </div>
            </>
          }
        />
      )}

      {loading ? (
        <div className="card text-center py-16">
          <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando diplomados...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-graduation-cap-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">No se encontraron diplomados</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((diplomado) => {
            const { enrolled, attRate, compRate } = getEnrollmentStats(diplomado.id);
            const editionCount = diplomado._count?.editions ?? diplomado.editions?.length ?? 0;
            return (
              <div key={diplomado.id} className="card hover:border-[var(--color-border-light)] group">
                <div className="flex gap-4 mb-4">
                  <img
                    src={courseImageUrl(diplomado.image_url)}
                    alt={diplomado.name}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-[var(--color-border)]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 onClick={() => navigate(`/diplomados/${diplomado.id}`)} className="text-sm font-semibold text-[var(--color-text-primary)] truncate cursor-pointer hover:text-[var(--color-text-accent)]">
                        {diplomado.name}
                      </h3>
                      {diplomado.deleted_at ? (
                        <span className="badge-danger bg-red-500/20 text-red-500">Eliminado</span>
                      ) : diplomado.active ? (
                        <span className="badge-success">Activo</span>
                      ) : (
                        <span className="badge-warning">Inactivo</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">{diplomado.description}</p>
                    <span className="badge-info text-[10px] mt-2 inline-block">{diplomado.category}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                  <div>
                    <span className="text-[var(--color-text-muted)]">Duración:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{diplomado.duration}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Horas:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{diplomado.hours}h</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Ediciones:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{editionCount}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Inscritos:</span>
                    <p className="text-[var(--color-text-accent)] font-semibold">{enrolled}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[var(--color-text-primary)]">{enrolled}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Estudiantes</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[var(--color-text-accent)]">{attRate}%</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Asistencia</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-green-400">{compRate}%</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Completado</p>
                  </div>
                </div>

                <div className="border-t border-[var(--color-border)] pt-4 flex items-center justify-between">
                  <label className={`flex items-center gap-2 text-xs text-[var(--color-text-secondary)] ${diplomado.deleted_at ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      checked={diplomado.show_on_landing}
                      disabled={togglingId === diplomado.id || !!diplomado.deleted_at}
                      onChange={() => handleToggleLanding(diplomado)}
                    />
                    Mostrar en landing
                  </label>
                  {diplomado.deleted_at ? (
                    <button onClick={() => handleRestore(diplomado.id)} className="text-xs text-green-500 hover:text-green-400 font-semibold inline-flex items-center gap-1 cursor-pointer">
                      <i className="ri-refresh-line"></i> Recuperar
                    </button>
                  ) : (
                    <button onClick={() => navigate(`/diplomados/${diplomado.id}`)} className="text-xs text-[var(--color-text-accent)] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 cursor-pointer">
                      Ver detalle <i className="ri-arrow-right-s-line"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NuevoDiplomadoModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onCreated={loadData} />
    </div>
  );
}
